import { ReactNode } from 'react';
import {
  ComponentRendering,
  constants,
  GetServerSideComponentProps,
  LayoutServiceData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { BreadcrumbModel, N05BreadcrumbProps } from './N05Breadcrumb.types';
import { CreateBreadcrumbModel } from './N05Breadcrumb.service';
import { Breadcrumbs } from '@ahc-digital/component-library';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { GetServerSidePropsContext } from 'next';
import { ImportedData, ImportedDataType } from 'lib/imported-data/ImportedData.types';
import { sparkleUtils } from 'src/shared/sparkle-utils';
import { ImportedDataProviderFactory } from 'lib/imported-data';
import { SparklePageId, SPARKLE_PAGE_IDS } from 'src/shared/static';

export const Default = (props?: N05BreadcrumbProps): JSX.Element => {
  const id = props?.params?.RenderingIdentifier;
  const showOnMobile = props?.rendering?.params?.ShowOnMobile;
  const model: BreadcrumbModel = CreateBreadcrumbModel(props?.fields?.data);
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  const getBreadcrumb = () => {
    const templateId = props?.layoutData?.sitecore?.route?.templateId?.toLowerCase() || '';

    const sparkleTemplateIds = [
      SPARKLE_PAGE_IDS.Provider,
      SPARKLE_PAGE_IDS.Location,
      SPARKLE_PAGE_IDS.Event,
    ].map((id) => id.toLowerCase());

    const currentPage = model.currentPage;
    const importedTitle = props?.importedData?.navigationTitle;
    const pageTitle = currentPage?.navigationTitle?.value;
    const pageName = currentPage?.name;

    const breadcrumb =
      templateId && sparkleTemplateIds.includes(templateId)
        ? importedTitle || pageTitle || pageName
        : pageTitle || pageName;

    return breadcrumb || '';
  };
  const breadcrumb: string = getBreadcrumb();

  const links = (model?.ancestors?.map((ancestor, idx) => (
    <a key={idx} target="_self" href={ancestor?.url?.path}>
      {ancestor?.navigationTitle?.value || ancestor?.name || ''}
    </a>
  )) || []) as ReactNode[];

  return (
    <Breadcrumbs
      id={id ? id : undefined}
      links={links}
      currentPage={breadcrumb}
      showOnMobile={Boolean(showOnMobile)}
      data-elastic-exclude=""
      data-component={marketingRenderingName}
    />
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  _rendering: ComponentRendering,
  _layoutData: LayoutServiceData,
  context: GetServerSidePropsContext
) => {
  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return { importedData: {} as ImportedData, layoutData: _layoutData };
  }
  const slug = sparkleUtils.getSlug(context.req.url ?? '');
  if (sparkleUtils.isEmptyOrWhiteSpace(slug)) return;

  const provider = ImportedDataProviderFactory.create();

  const pageTypeMap: Record<SparklePageId, ImportedDataType> = {
    [SPARKLE_PAGE_IDS.Provider]: ImportedDataType.SparkleProvider,
    [SPARKLE_PAGE_IDS.Location]: ImportedDataType.SparkleLocation,
    [SPARKLE_PAGE_IDS.Event]: ImportedDataType.SparkleEvent,
  };

  const templateId = _layoutData?.sitecore?.route?.templateId as SparklePageId | undefined;

  const sparkleType =
    templateId && templateId in pageTypeMap
      ? pageTypeMap[templateId]
      : ImportedDataType.SparkleProvider;

  const result = await provider.getDataFor(slug, sparkleType);

  return { importedData: result, layoutData: _layoutData };
};
